import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  PaperPlaneRight, ArrowLeft, Trash, SoccerBall,
  ChartLineUp, Books, Lightning, Question,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Suggested quick-reply questions ───────────────────────────────────────────
const SUGGESTED = [
  { icon: "⚽", label: "¿Qué es un 2x1 (parlay)?" },
  { icon: "📊", label: "Explícame las cuotas decimales" },
  { icon: "🎯", label: "¿Qué es el handicap?" },
  { icon: "💰", label: "¿Cómo manejo mi bankroll?" },
  { icon: "📱", label: "¿Cómo hago una apuesta en Bet365?" },
  { icon: "⬆️", label: "¿Qué significa Over/Under?" },
  { icon: "🔄", label: "¿Cómo funciona un acumulador?" },
  { icon: "💡", label: "¿Qué es una value bet?" },
];

// ── Markdown-lite renderer for bot messages ────────────────────────────────────
function BotMessage({ text }) {
  // Convert **bold**, `code`, ## headers, - bullets and numbered lists
  const html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.*?)`/g, '<code style="background:rgba(212,175,55,0.15);padding:1px 5px;border-radius:4px;font-size:0.85em;font-family:monospace">$1</code>')
    .replace(/^## (.+)$/gm, '<div style="font-weight:900;color:#D4AF37;margin:10px 0 4px;font-size:0.92em;letter-spacing:0.05em;text-transform:uppercase">$1</div>')
    .replace(/^### (.+)$/gm, '<div style="font-weight:700;color:#e0d0a0;margin:8px 0 3px;font-size:0.88em">$1</div>')
    .replace(/^\d+\. (.+)$/gm, '<div style="display:flex;gap:8px;margin:2px 0"><span style="color:#D4AF37;font-weight:700;min-width:16px">$0</span></div>')
    .replace(/^[-•] (.+)$/gm, '<div style="display:flex;gap:8px;margin:2px 0"><span style="color:#D4AF37;margin-top:2px;flex-shrink:0">▸</span><span>$1</span></div>')
    .replace(/\n\n/g, '<div style="height:8px"></div>')
    .replace(/\n/g, "<br/>");
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

// ── Typing indicator ───────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-4 py-3 rounded-2xl rounded-bl-sm" style={{ background: "#111117", width: "fit-content" }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 7, height: 7, borderRadius: "50%", background: "#D4AF37",
          animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
  );
}

// ── Main ChatPage ──────────────────────────────────────────────────────────────
export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem("betiq_chat_session");
    if (stored) return stored;
    const id = crypto.randomUUID();
    localStorage.setItem("betiq_chat_session", id);
    return id;
  });

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // ── Load history on mount ────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/chat/history/${sessionId}`)
      .then(r => {
        if (r.data.messages?.length) {
          setMessages(r.data.messages.map(m => ({ role: m.role, text: m.content })));
        }
      })
      .catch(() => {});
  }, [sessionId]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Send message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/chat/message`, {
        session_id: sessionId,
        message: msg,
      });
      setMessages(prev => [...prev, { role: "assistant", text: data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Error de conexión. Intenta de nuevo.";
      setMessages(prev => [...prev, { role: "assistant", text: `⚠️ ${detail}`, isError: true }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, loading, sessionId]);

  // ── Clear session ─────────────────────────────────────────────────────────
  const clearChat = async () => {
    await axios.delete(`${API}/api/chat/session/${sessionId}`).catch(() => {});
    setMessages([]);
  };

  const hasMessages = messages.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: "#050508", overflow: "hidden" }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ background: "#070709", borderBottom: "1px solid rgba(212,175,55,0.12)", minHeight: 60 }}
        data-testid="chat-header"
      >
        <div className="flex items-center gap-3">
          <Link to="/" className="p-1" data-testid="chat-back">
            <ArrowLeft className="w-5 h-5" style={{ color: "#A1A1AA" }} />
          </Link>
          <div className="relative flex-shrink-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)" }}>
              <SoccerBall weight="fill" className="w-4 h-4" style={{ color: "#000" }} />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2"
              style={{ background: "#10B981", borderColor: "#070709" }} />
          </div>
          <div>
            <div className="text-sm font-black text-white" data-testid="chat-bot-name">Asesor GCR</div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#10B981" }}>En línea</div>
          </div>
        </div>
        {hasMessages && (
          <button onClick={clearChat} className="p-2 rounded-lg"
            style={{ color: "#A1A1AA" }}
            title="Limpiar conversación" data-testid="chat-clear">
            <Trash className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* ── Messages area ──────────────────────────────────────── */}
      <div
        style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "12px 16px", minHeight: 0 }}
        data-testid="chat-messages"
      >
        {/* Welcome state */}
        {!hasMessages && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, textAlign: "center", padding: "24px 0" }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
              background: "linear-gradient(135deg, rgba(212,175,55,0.2), rgba(212,175,55,0.05))",
              border: "1px solid rgba(212,175,55,0.2)" }}>
              <SoccerBall weight="fill" className="w-7 h-7" style={{ color: "#D4AF37" }} />
            </div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, color: "#fff", fontSize: "1.25rem", marginBottom: 8 }}>Asesor GCR</h2>
            <p style={{ color: "#A1A1AA", fontSize: "0.85rem", maxWidth: 280, marginBottom: 20, lineHeight: 1.5 }}>
              Tu guía de apuestas. Pregúntame sobre términos, estrategias o cómo colocar una apuesta paso a paso.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 300, marginBottom: 20 }}>
              {[
                { icon: <Books className="w-3.5 h-3.5" />, label: "Educación" },
                { icon: <ChartLineUp className="w-3.5 h-3.5" />, label: "Estrategia" },
                { icon: <Lightning className="w-3.5 h-3.5" />, label: "Paso a paso" },
                { icon: <Question className="w-3.5 h-3.5" />, label: "Cualquier duda" },
              ].map(f => (
                <div key={f.label} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                  borderRadius: 999, fontSize: "0.75rem", fontWeight: 600,
                  background: "rgba(212,175,55,0.07)", color: "#D4AF37",
                  border: "1px solid rgba(212,175,55,0.15)"
                }}>
                  {f.icon} {f.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Message bubbles */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 680, margin: "0 auto" }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-start" }}
              data-testid={`chat-msg-${msg.role}`}>
              {msg.role === "assistant" && (
                <div style={{ width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                  marginRight: 8, marginTop: 2, flexShrink: 0,
                  background: "linear-gradient(135deg, #D4AF37, #B8962E)" }}>
                  <SoccerBall weight="fill" style={{ width: 14, height: 14, color: "#000" }} />
                </div>
              )}
              <div style={{ maxWidth: "82%", minWidth: 0 }}>
                <div style={{
                  padding: "10px 14px",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  ...(msg.role === "user"
                    ? {
                        background: "linear-gradient(135deg, #D4AF37, #B8962E)",
                        color: "#000", fontWeight: 600,
                        borderRadius: "18px 18px 4px 18px",
                      }
                    : {
                        background: msg.isError ? "rgba(239,68,68,0.1)" : "#111117",
                        color: msg.isError ? "#ef4444" : "#E4E4E7",
                        border: msg.isError ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(255,255,255,0.05)",
                        borderRadius: "18px 18px 18px 4px",
                      }
                  ),
                }}>
                  {msg.role === "assistant" ? <BotMessage text={msg.text} /> : msg.text}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start" }} data-testid="chat-typing">
              <div style={{ width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                marginRight: 8, flexShrink: 0, background: "linear-gradient(135deg, #D4AF37, #B8962E)" }}>
                <SoccerBall weight="fill" style={{ width: 14, height: 14, color: "#000" }} />
              </div>
              <TypingDots />
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* ── Suggested questions ─────────────────────────────────── */}
      {!hasMessages && (
        <div style={{ flexShrink: 0, padding: "0 16px 8px", maxWidth: 680, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
          <div style={{ fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 900, color: "#71717A", textAlign: "center", marginBottom: 8 }}>
            Preguntas frecuentes
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {SUGGESTED.map(s => (
              <button key={s.label} onClick={() => sendMessage(s.label)}
                style={{
                  textAlign: "left", padding: "10px 12px", borderRadius: 12,
                  fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
                  background: "#0E0E13", color: "#A1A1AA", border: "1px solid #1A1A24",
                  transition: "all 0.15s", wordBreak: "break-word",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#111117"; e.currentTarget.style.color = "#D4AF37"; e.currentTarget.style.borderColor = "rgba(212,175,55,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#0E0E13"; e.currentTarget.style.color = "#A1A1AA"; e.currentTarget.style.borderColor = "#1A1A24"; }}
                data-testid="chat-suggestion">
                <span style={{ marginRight: 6 }}>{s.icon}</span>{s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Input area ─────────────────────────────────────────── */}
      <div
        style={{ flexShrink: 0, padding: "10px 16px 20px", background: "#070709", borderTop: "1px solid rgba(255,255,255,0.05)" }}
        data-testid="chat-input-area"
      >
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, maxWidth: 680, margin: "0 auto" }}>
          <div style={{
            flex: 1, display: "flex", alignItems: "flex-end",
            borderRadius: 20, padding: "10px 14px",
            background: "#0E0E13", border: "1px solid #1A1A24",
            minHeight: 46, boxSizing: "border-box",
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Pregunta sobre apuestas..."
              rows={1}
              style={{
                flex: 1, background: "transparent", color: "#fff", border: "none", outline: "none",
                fontSize: "0.875rem", resize: "none", lineHeight: 1.5, maxHeight: 100,
                fontFamily: "inherit",
              }}
              data-testid="chat-input"
            />
          </div>
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            style={{
              flexShrink: 0, width: 44, height: 44, borderRadius: 14,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: input.trim() && !loading
                ? "linear-gradient(135deg, #D4AF37, #B8962E)"
                : "#111117",
              border: "1px solid rgba(255,255,255,0.05)",
              cursor: input.trim() && !loading ? "pointer" : "not-allowed",
              transition: "all 0.15s",
            }}
            data-testid="chat-send">
            <PaperPlaneRight weight="fill" style={{ width: 16, height: 16, color: input.trim() && !loading ? "#000" : "#333" }} />
          </button>
        </div>
        <p style={{ textAlign: "center", fontSize: "0.625rem", marginTop: 8, color: "#71717A" }}>
          Asesor GCR · Apuesta responsablemente
        </p>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
